"use client";

import { useState } from "react";
import { Modal, ModalContent, ModalBody, Button, Card, CardBody } from "@heroui/react";
import { 
  CheckCircle, 
  Users, 
  Gift, 
  Copy, 
  AlertCircle, 
  X 
} from "lucide-react";
import { toast } from "sonner";

interface ReferralModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (code: string) => void;
  referralCode: string;
  isLoading: boolean;
  hasActiveReferral: boolean;
  address: string;
}

export const ReferralModal = ({
  isOpen,
  onClose,
  onApply,
  referralCode,
  isLoading,
  hasActiveReferral,
  address
}: ReferralModalProps) => {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalLoading, setExternalLoading] = useState(false);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleApply = async () => {
    if (!referralCode.trim()) {
      setError("Please enter a valid referral code");
      return;
    }

    try {
      setExternalLoading(true);
      await onApply(referralCode);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to apply referral code");
    } finally {
      setExternalLoading(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      toast.success("Referral code copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      toast.error("Failed to copy referral code");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="2xl"
      placement="center"
      scrollBehavior="inside"
      hideCloseButton
      classNames={{
        backdrop: "bg-black/50 backdrop-blur-sm",
        wrapper: "z-50 flex items-center justify-center",
        base: "bg-[#0C1113] text-white mx-4 my-4 max-h-[90vh] rounded-xl",
        body: "p-0",
        header: "hidden",
      }}
    >
      <ModalContent>
        {() => (
          <>
            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-50 w-8 h-8 flex items-center justify-center text-white hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {error ? (
              <>
                {/* Header with Error Icon */}
                <div className="flex items-center gap-3 p-6 pb-4">
                  <div className="p-2 bg-red-500/10 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Error</h2>
                  </div>
                </div>
                
                <ModalBody className="p-0">
                  <div className="p-6">
                    <Card className="bg-red-500/10 border border-red-500/20">
                      <CardBody className="p-4">
                        <p className="text-red-400 text-sm">{error}</p>
                      </CardBody>
                    </Card>
                    <div className="flex justify-end mt-4">
                      <Button
                        color="danger"
                        variant="light"
                        onPress={handleClose}
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </ModalBody>
              </>
            ) : hasActiveReferral ? (
              <>
                {/* Header with Success Icon */}
                <div className="flex items-center gap-3 p-6 pb-4">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Referral Active</h2>
                  </div>
                </div>
                
                <ModalBody className="p-0">
                  <div className="p-6">
                    <Card className="bg-green-500/10 border border-green-500/20">
                      <CardBody className="p-4 text-center">
                        <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-white mb-2">
                          Referral Code Applied!
                        </h3>
                        <p className="text-gray-300 text-sm mb-4">
                          You&apos;re now earning bonus points with your friend. Both of you will benefit from this referral!
                        </p>
                        <Button
                          color="success"
                          variant="light"
                          onPress={handleClose}
                        >
                          Continue
                        </Button>
                      </CardBody>
                    </Card>
                  </div>
                </ModalBody>
              </>
            ) : (
              <>
                {/* Header with Users Icon */}
                <div className="flex items-center gap-3 p-6 pb-4">
                  <div className="p-2 bg-[#60FF8A]/10 rounded-lg">
                    <Users className="w-5 h-5 text-[#60FF8A]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Apply Referral Code</h2>
                  </div>
                </div>
                
                <ModalBody className="p-0">
                  <div className="p-6">
                    <div className="space-y-6">
                      {/* Referral Info Card */}
                      <Card className="bg-[#0C1113] border border-gray-800/50">
                        <CardBody className="p-4">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-[#60FF8A]/10 rounded-lg">
                              <Gift className="w-5 h-5 text-[#60FF8A]" />
                            </div>
                            <div>
                              <h3 className="text-lg font-semibold text-white">Referral Bonus</h3>
                              <p className="text-sm text-gray-400">Earn extra points together</p>
                            </div>
                          </div>
                          <p className="text-gray-300 text-sm">
                            You&apos;re joining through a referral link. Both you and your friend will earn bonus points!
                          </p>
                        </CardBody>
                      </Card>

                      {/* Referral Code Display */}
                      <Card className="bg-[#0C1113] border border-gray-800/50">
                        <CardBody className="p-4">
                          <h4 className="text-sm font-semibold text-gray-300 mb-3">Referral Code</h4>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-700 px-3 py-2 rounded-lg">
                              <code className="text-[#60FF8A] font-mono text-sm">{referralCode}</code>
                            </div>
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={handleCopyCode}
                              className="min-w-unit-8 w-8 h-8"
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardBody>
                      </Card>

                      {/* Apply Button */}
                      <div className="flex justify-center">
                        <Button
                          size="lg"
                          variant="solid"
                          onPress={handleApply}
                          isLoading={isLoading || externalLoading}
                          className="bg-[#60FF8A] text-black font-semibold hover:bg-[#60FF8A]/90"
                        >
                          Apply Code
                        </Button>
                      </div>
                    </div>
                  </div>
                </ModalBody>
              </>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};